import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, BadgeCheck, Clock, IndianRupee, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import Nav from "@/components/site/Nav";
import Footer from "@/components/site/Footer";
import WhatsAppFloat from "@/components/site/WhatsAppFloat";
import { MaskedLine, Reveal } from "@/components/site/Reveal";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api, formatApiErrorDetail } from "@/api";
import { waLink } from "@/config";

const steps = [
  { icon: BadgeCheck, title: "Free Inspection", desc: "Showroom par lao ya hum aa jayenge — 20 minute ka full checkup, bilkul free." },
  { icon: IndianRupee, title: "Fair Valuation", desc: "Market-based on-spot price. Jo bola, wahi milega. No hidden deductions." },
  { icon: Clock, title: "Instant Payment", desc: "Deal pakki hote hi payment — cash, UPI ya bank transfer. Same day." },
];

const initial = { name: "", phone: "", bike_model: "", year: "", km: "", expected_price: "", message: "" };

const SellBike = () => {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/sell-requests", {
        name: form.name,
        phone: form.phone,
        bike_model: form.bike_model,
        year: form.year ? Number(form.year) : null,
        km: form.km ? Number(form.km) : null,
        expected_price: form.expected_price ? Number(form.expected_price) : null,
        message: form.message,
      });
      toast.success("Request mil gayi! Hum jald contact karenge.");
      setForm(initial);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-[#050505] text-white min-h-screen" data-testid="sell-page">
      <Nav />
      <WhatsAppFloat />

      <section className="max-w-7xl mx-auto px-5 md:px-8 pt-32 md:pt-40 pb-14">
        <p className="text-volt uppercase tracking-[0.3em] text-xs font-semibold mb-4">Sell Your Two-Wheeler</p>
        <h1 className="font-display font-semibold uppercase tracking-tighter leading-[0.95] text-5xl md:text-7xl" data-testid="sell-heading">
          <MaskedLine delay={0.1}>Bechna hai?</MaskedLine>
          <MaskedLine delay={0.25} className="text-volt">Best price lo.</MaskedLine>
        </h1>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7 }}
          className="mt-5 max-w-lg text-white/60 leading-relaxed"
        >
          Form bharo ya seedha WhatsApp karo — free inspection, fair valuation aur instant payment. Itna simple.
        </motion.p>
      </section>

      <section className="max-w-7xl mx-auto px-5 md:px-8 pb-16" data-testid="sell-steps">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {steps.map((s, i) => (
            <Reveal key={s.title} delay={i * 0.12}>
              <div className="border border-white/10 bg-card p-7 h-full">
                <s.icon className="w-8 h-8 text-volt mb-5" />
                <h3 className="font-display font-semibold uppercase tracking-tight text-lg">{s.title}</h3>
                <p className="mt-2 text-white/55 text-sm leading-relaxed">{s.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 md:px-8 pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
          <Reveal className="lg:col-span-3">
            <form onSubmit={handleSubmit} className="border border-white/10 bg-card p-7 md:p-9 space-y-6" data-testid="sell-form">
              <h2 className="font-display font-semibold uppercase tracking-tight text-2xl">Bike details batao</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <Label htmlFor="sell-name" className="text-xs uppercase tracking-widest text-white/50">Your Name *</Label>
                  <Input id="sell-name" data-testid="sell-name-input" required value={form.name} onChange={set("name")} placeholder="Rahul Kumar" className="bg-[#0a0a0a] border-white/15 text-white rounded-none h-12" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="sell-phone" className="text-xs uppercase tracking-widest text-white/50">Phone Number *</Label>
                  <Input id="sell-phone" data-testid="sell-phone-input" required value={form.phone} onChange={set("phone")} placeholder="98765 43210" className="bg-[#0a0a0a] border-white/15 text-white rounded-none h-12" />
                </div>
                <div className="space-y-2 sm:col-span-2">
                  <Label htmlFor="sell-model" className="text-xs uppercase tracking-widest text-white/50">Bike / Scooter Model *</Label>
                  <Input id="sell-model" data-testid="sell-model-input" required value={form.bike_model} onChange={set("bike_model")} placeholder="e.g. Honda Activa 6G" className="bg-[#0a0a0a] border-white/15 text-white rounded-none h-12" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="sell-year" className="text-xs uppercase tracking-widest text-white/50">Year</Label>
                  <Input id="sell-year" data-testid="sell-year-input" type="number" min="1990" max="2026" value={form.year} onChange={set("year")} placeholder="2021" className="bg-[#0a0a0a] border-white/15 text-white rounded-none h-12" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="sell-km" className="text-xs uppercase tracking-widest text-white/50">KM Driven</Label>
                  <Input id="sell-km" data-testid="sell-km-input" type="number" min="0" value={form.km} onChange={set("km")} placeholder="15000" className="bg-[#0a0a0a] border-white/15 text-white rounded-none h-12" />
                </div>
                <div className="space-y-2 sm:col-span-2">
                  <Label htmlFor="sell-price" className="text-xs uppercase tracking-widest text-white/50">Expected Price (₹)</Label>
                  <Input id="sell-price" data-testid="sell-price-input" type="number" min="0" value={form.expected_price} onChange={set("expected_price")} placeholder="60000" className="bg-[#0a0a0a] border-white/15 text-white rounded-none h-12" />
                </div>
                <div className="space-y-2 sm:col-span-2">
                  <Label htmlFor="sell-message" className="text-xs uppercase tracking-widest text-white/50">Anything Else?</Label>
                  <Textarea id="sell-message" data-testid="sell-message-input" value={form.message} onChange={set("message")} placeholder="Condition, modifications, service history..." className="bg-[#0a0a0a] border-white/15 text-white rounded-none min-h-[110px]" />
                </div>
              </div>
              <button
                type="submit"
                disabled={submitting}
                data-testid="sell-submit-button"
                className="w-full inline-flex items-center justify-center gap-2 bg-volt text-black font-semibold uppercase tracking-widest px-7 py-4 hover:brightness-110 disabled:opacity-50 transition-[filter] duration-300"
              >
                {submitting ? "Sending..." : <>Get My Valuation <ArrowRight className="w-4 h-4" /></>}
              </button>
            </form>
          </Reveal>

          <Reveal delay={0.15} className="lg:col-span-2">
            <div className="border border-volt/30 bg-volt/5 p-7 md:p-9 h-full flex flex-col justify-between" data-testid="sell-whatsapp-card">
              <div>
                <h3 className="font-display font-semibold uppercase tracking-tight text-2xl">Jaldi hai?</h3>
                <p className="mt-3 text-white/60 text-sm leading-relaxed">
                  Form ka wait mat karo. Apni bike ki photos ke saath seedha WhatsApp karo — 30 minute mein estimated value mil jayegi.
                </p>
                <ul className="mt-6 space-y-3 text-sm text-white/70">
                  <li className="flex gap-3"><span className="text-volt font-semibold">01</span> Bike ki 4-5 photos bhejo</li>
                  <li className="flex gap-3"><span className="text-volt font-semibold">02</span> Model, year, km batao</li>
                  <li className="flex gap-3"><span className="text-volt font-semibold">03</span> Estimated value turant pao</li>
                </ul>
              </div>
              <a
                href={waLink("Hi Deal 2 Wheel! I want to sell my two-wheeler. Here are the details:")}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="sell-whatsapp-cta"
                className="mt-8 inline-flex items-center justify-center gap-2 bg-volt text-black font-semibold px-6 py-4 rounded-full hover:scale-[1.03] volt-glow transition-transform duration-300"
              >
                <MessageCircle className="w-5 h-5" /> WhatsApp Karo
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SellBike;
